import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 6) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/subscription/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("success");
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-28 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto animate-spin" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Confirming your membership…</h1>
            <p className="mt-3 text-white/50 font-mono text-sm">Verifying payment with Stripe.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto" />
            <h1 className="mt-6 font-display text-3xl font-bold text-white">You're a Founding Member.</h1>
            <p className="mt-3 text-white/60">Full access unlocked — every case workspace, the AI Assistant, and Detective Notes.</p>
            <button onClick={() => navigate("/cases")} data-testid="goto-cases-btn"
              className="mt-8 px-6 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
              Explore Cases
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Payment not confirmed</h1>
            <p className="mt-3 text-white/60">{status === "timeout" ? "It's taking longer than expected. Check your dashboard shortly." : "We couldn't confirm your payment."}</p>
            <Link to="/pricing" className="mt-8 inline-block px-6 py-3 border border-white/15 text-white rounded-sm hover:border-gold/40 transition-colors">Back to Pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
